import React from 'react';
import { useMediaQuery } from '@mui/material';
import { ResponsiveDialog } from '../../Components/Dialog';

interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Brand-theme colors. Callers forward values from their surrounding widget
   * state so the dialog matches the rest of the surface:
   *  - backgroundColor:        dialog surface (e.g. page / sidebar background)
   *  - primaryColor:           title text + cancel text + cancel border + confirm bg
   *  - primaryForegroundColor: confirm button text (contrast on primaryColor)
   */
  backgroundColor?: string;
  primaryColor?: string;
  primaryForegroundColor?: string;
  fontFamily?: string;
}

/**
 * Destructive-action confirmation dialog used by the concierge surfaces.
 *
 * Desktop (matches ChatHistoryPage reference):
 *  - left-aligned title/message
 *  - inline row of buttons on the right: outlined Cancel, then filled Confirm
 *
 * Mobile (matches Figma node 3468-5161):
 *  - centered title/message
 *  - full-width pill buttons stacked vertically: filled Confirm on top,
 *    outlined Cancel below (thumb-reachable primary action)
 *
 * Rendered through `ResponsiveDialog` so it's a centered modal on desktop and
 * a bottom sheet on mobile. Going through MUI's portal also avoids the
 * `position: fixed` clipping when mounted inside a transformed ancestor
 * (e.g. the mobile sidebar's translateX slide-in).
 */
export const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmDisabled = false,
  onConfirm,
  onCancel,
  backgroundColor = '#ffffff',
  primaryColor = '#0d2340',
  primaryForegroundColor = '#ffffff',
  fontFamily,
}) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

  const cancelButton = (
    <button
      type="button"
      onClick={onCancel}
      style={{
        padding: '0 24px',
        border: `1px solid ${primaryColor}`,
        borderRadius: 100,
        backgroundColor,
        color: primaryColor,
        cursor: 'pointer',
        fontSize: isMobile ? 15 : 14,
        fontWeight: 600,
        lineHeight: '22px',
        height: isMobile ? 48 : 40,
        width: isMobile ? '100%' : undefined,
        boxSizing: 'border-box',
        fontFamily: 'inherit',
      }}
    >
      {cancelText}
    </button>
  );

  const confirmButton = (
    <button
      type="button"
      onClick={onConfirm}
      disabled={confirmDisabled}
      style={{
        padding: '0 24px',
        border: `1px solid ${primaryColor}`,
        borderRadius: 100,
        backgroundColor: primaryColor,
        color: primaryForegroundColor,
        cursor: confirmDisabled ? 'not-allowed' : 'pointer',
        fontSize: isMobile ? 15 : 14,
        fontWeight: 600,
        lineHeight: '22px',
        height: isMobile ? 48 : 40,
        width: isMobile ? '100%' : undefined,
        boxSizing: 'border-box',
        opacity: confirmDisabled ? 0.7 : 1,
        fontFamily: 'inherit',
      }}
    >
      {confirmText}
    </button>
  );

  return (
    <ResponsiveDialog
      open={open}
      onClose={onCancel}
      disableScrollLock
      sx={(theme) => ({
        zIndex: 10000,
        // Hide ResponsiveDialog's desktop close (✕) icon — the Chat History /
        // desktop spec for this dialog uses backdrop-click + Cancel only.
        '& .bc_dialog_close_button': { display: 'none' },
        '& div.MuiDialog-paper': {
          margin: '0 20px',
          width: '100%',
          maxWidth: 584,
          // `minHeight` (not a fixed `height`) so longer bodies — e.g. the
          // voice mic-permission error — grow to fit instead of clipping /
          // scrolling. Shorter confirmations still render at the 172 baseline.
          minHeight: 172,
          backgroundColor,
          paddingTop: 0,
          borderRadius: '10px',
          [theme.breakpoints.down('md')]: {
            maxWidth: '100%',
            width: '100%',
            height: 'auto',
            margin: 0,
            // Match ShareConversationDialog's 12px paper offset above the
            // inner 40px padding for a consistent sheet header gap.
            paddingTop: '12px',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
      })}
    >
      <div
        style={{
          // Mobile padding/typography mirror ShareConversationDialog so the
          // two sheet-style dialogs feel like the same component family.
          padding: isMobile ? 40 : '24px 15px 16px 24px',
          backgroundColor,
          ...(fontFamily ? { fontFamily } : {}),
        }}
      >
        <p
          style={{
            margin: 0,
            color: primaryColor,
            fontSize: isMobile ? 24 : 16,
            fontWeight: isMobile ? 600 : 500,
            lineHeight: isMobile ? '25px' : '24px',
            textAlign: isMobile ? 'center' : 'left',
          }}
        >
          {title}
        </p>
        <div
          style={{
            marginTop: isMobile ? 16 : 8,
            color: primaryColor,
            fontSize: 16,
            fontWeight: 500,
            lineHeight: isMobile ? 1.5 : '24px',
            textAlign: isMobile ? 'center' : 'left',
          }}
        >
          {message}
        </div>
        <div
          style={
            isMobile
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  marginTop: 16,
                }
              : {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 8,
                  marginTop: 36,
                }
          }
        >
          {isMobile ? (
            <>
              {confirmButton}
              {cancelButton}
            </>
          ) : (
            <>
              {cancelButton}
              {confirmButton}
            </>
          )}
        </div>
      </div>
    </ResponsiveDialog>
  );
};
